import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent
} from "@/components/ui/tabs";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext
} from "@/components/ui/carousel";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from "@/components/ui/dialog";
import PageHeader from "@/components/common/PageHeader";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Quote, Star } from "lucide-react";
import api from "@/lib/api";

const blank = {
  clientName: "",
  clientDesignation: "",
  companyName: "",
  rating: 5,
  testimonialText: "",
  profileImageUrl: "",
  displayOrder: 1
};

export default function Testimonials() {
  const [items, setItems] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchTestimonials = async () => {
    try {
      setLoading(true);
      const res = await api.get("/Testimonial");
      if (res.success) {
        setItems(res.data || []);
      } else {
        toast.error(res.message || "Failed to fetch testimonials");
      }
    } catch (err) {
      toast.error(err?.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const save = async () => {
    if (!form.clientName || !form.testimonialText) {
      toast.error("Name and testimonial are required.");
      return;
    }
    
    try {
      if (editing) {
        const res = await api.put(`/Testimonial/${editing.id}`, form);
        if (res.success) {
          toast.success("Testimonial updated.");
          fetchTestimonials();
          setModalOpen(false);
        } else {
          toast.error(res.message || "Update failed");
        }
      } else {
        const res = await api.post("/Testimonial", form);
        if (res.success) {
          toast.success("Testimonial added.");
          fetchTestimonials();
          setModalOpen(false);
        } else {
          toast.error(res.message || "Creation failed");
        }
      }
    } catch (err) {
      toast.error(err?.message || "An error occurred");
    }
  };

  const handleDelete = async () => {
    try {
      const res = await api.delete(`/Testimonial/${toDelete.id}`);
      if (res.success) {
        toast.success("Testimonial removed.");
        fetchTestimonials();
      } else {
        toast.error(res.message || "Failed to delete");
      }
    } catch (err) {
      toast.error(err?.message || "An error occurred");
    } finally {
      setConfirmOpen(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Testimonials"
        description="Curate kind words from people you've worked with."
        testId="testimonials"
        action={() => { setEditing(null); setForm(blank); setModalOpen(true); }}
        actionLabel="Add Testimonial"
        actionIcon={Plus}
      />

      <Tabs defaultValue="grid" className="space-y-5" data-testid="testimonials-tabs">
        <TabsList>
          <TabsTrigger value="grid" data-testid="testimonials-grid-tab">Card view</TabsTrigger>
          <TabsTrigger value="carousel" data-testid="testimonials-carousel-tab">Carousel</TabsTrigger>
        </TabsList>

        <TabsContent value="grid">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((t) => (
              <Card key={t.id} data-testid={`testimonial-card-${t.id}`} className="group p-6 hover:border-foreground/20 transition-colors">
                <Quote className="h-6 w-6 text-primary/30" strokeWidth={1.5} />
                <p className="text-sm leading-relaxed mt-3 text-foreground/90">&ldquo;{t.testimonialText}&rdquo;</p>
                <div className="flex items-center gap-0.5 mt-4">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star
                      key={i}
                      className={`h-3.5 w-3.5 ${i < t.rating ? "fill-primary text-primary" : "text-muted"}`}
                      strokeWidth={1.5}
                    />
                  ))}
                </div>
                <div className="flex items-center gap-3 mt-5 pt-5 border-t border-border">
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={t.profileImageUrl} alt={t.clientName} />
                    <AvatarFallback>{t.clientName ? t.clientName.split(" ").map((n) => n[0]).join("") : "?"}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate">{t.clientName}</p>
                    <p className="text-[11px] font-mono text-muted-foreground truncate">{t.clientDesignation} · {t.companyName}</p>
                  </div>
                  <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => { setEditing(t); setForm(t); setModalOpen(true); }} data-testid={`testimonial-edit-${t.id}`}>
                      <Pencil className="h-3 w-3" strokeWidth={1.5} />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7 text-destructive"
                      onClick={() => { setToDelete(t); setConfirmOpen(true); }}
                      data-testid={`testimonial-delete-${t.id}`}
                    >
                      <Trash2 className="h-3 w-3" strokeWidth={1.5} />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="carousel">
          <Card className="p-8 sm:p-12 relative overflow-hidden">
            <div className="absolute -top-12 -right-12 h-48 w-48 rounded-full bg-primary/5" />
            <Carousel className="relative" data-testid="testimonials-carousel">
              <CarouselContent>
                {items.map((t) => (
                  <CarouselItem key={t.id} className="md:basis-1/1">
                    <div className="max-w-2xl mx-auto text-center px-4">
                      <Quote className="h-10 w-10 text-primary/30 mx-auto" strokeWidth={1.5} />
                      <p className="font-display text-xl sm:text-2xl leading-relaxed mt-5 text-foreground/90">
                        &ldquo;{t.testimonialText}&rdquo;
                      </p>
                      <div className="flex items-center justify-center gap-0.5 mt-5">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <Star
                            key={i}
                            className={`h-4 w-4 ${i < t.rating ? "fill-primary text-primary" : "text-muted"}`}
                            strokeWidth={1.5}
                          />
                        ))}
                      </div>
                      <div className="flex items-center justify-center gap-3 mt-6">
                        <Avatar className="h-12 w-12">
                          <AvatarImage src={t.profileImageUrl} alt={t.clientName} />
                          <AvatarFallback>{t.clientName ? t.clientName.split(" ").map((n) => n[0]).join("") : "?"}</AvatarFallback>
                        </Avatar>
                        <div className="text-left">
                          <p className="text-sm font-semibold">{t.clientName}</p>
                          <p className="text-[11px] font-mono text-muted-foreground">{t.clientDesignation} · {t.companyName}</p>
                        </div>
                      </div>
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious className="left-0" />
              <CarouselNext className="right-0" />
            </Carousel>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-lg" data-testid="testimonial-modal">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? "Edit testimonial" : "Add testimonial"}</DialogTitle>
            <DialogDescription>Curate a kind word from someone you&apos;ve worked with.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="col-span-2 sm:col-span-1">
                <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Client Name</Label>
                <Input data-testid="testimonial-name" value={form.clientName} onChange={(e) => setForm({ ...form, clientName: e.target.value })} className="mt-1.5" />
              </div>
              <div className="col-span-2 sm:col-span-1">
                <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Designation</Label>
                <Input data-testid="testimonial-designation" value={form.clientDesignation} onChange={(e) => setForm({ ...form, clientDesignation: e.target.value })} className="mt-1.5" />
              </div>
              <div className="col-span-2 sm:col-span-1">
                <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Company</Label>
                <Input data-testid="testimonial-company" value={form.companyName} onChange={(e) => setForm({ ...form, companyName: e.target.value })} className="mt-1.5" />
              </div>
              <div className="col-span-2 sm:col-span-1">
                <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Rating</Label>
                <div className="mt-1.5 flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((r) => (
                    <button
                      key={r}
                      type="button"
                      onClick={() => setForm({ ...form, rating: r })}
                      data-testid={`testimonial-rating-${r}`}
                    >
                      <Star
                        className={`h-5 w-5 ${r <= form.rating ? "fill-primary text-primary" : "text-muted"} hover:scale-110 transition-transform`}
                        strokeWidth={1.5}
                      />
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Testimonial</Label>
              <Textarea data-testid="testimonial-text" value={form.testimonialText} onChange={(e) => setForm({ ...form, testimonialText: e.target.value })} rows={4} className="mt-1.5" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Profile Image URL</Label>
              <Input data-testid="testimonial-avatar" value={form.profileImageUrl} onChange={(e) => setForm({ ...form, profileImageUrl: e.target.value })} className="mt-1.5 font-mono text-xs" placeholder="https://…" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="testimonial-save">{editing ? "Save" : "Add"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Delete testimonial?"
        description="This will remove the testimonial from your portfolio."
        confirmLabel="Delete"
        onConfirm={handleDelete}
      />
    </div>
  );
}
